import React, { useEffect, useState } from "react";
import {
  StreakHistoryItem,
  useGetStreakHistoryQuery,
} from "../../../features/streak/streakSlice";

const PAGE_SIZE = 10;

interface StreakRewardHistoryProps {
  requiredDays: number;
}

export const formatStreakHistoryRow = (item: StreakHistoryItem) =>
  `₦${item.amount.toLocaleString()} · ${item.streakCount}-day streak · ${new Date(
    item.createdAt
  ).toLocaleDateString()}`;

export const StreakRewardHistory: React.FC<StreakRewardHistoryProps> = ({
  requiredDays,
}) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<StreakHistoryItem[]>([]);
  // `data` is the latest successful result regardless of arg, so page 1 stays
  // visible while page 2 loads; the effect appends when the new page lands.
  const { data, isLoading, isFetching, isError, refetch } =
    useGetStreakHistoryQuery({ page, limit: PAGE_SIZE });

  useEffect(() => {
    if (!data) return;
    setItems((prev) => {
      if (data.page === 1) return data.data;
      const seen = new Set(prev.map((i) => i._id));
      return [...prev, ...data.data.filter((i) => !seen.has(i._id))];
    });
  }, [data]);

  const hasMore = !!data && data.page < data.last_page;

  return (
    <div className="mt-6 pt-4 border-t border-gray-700">
      <h4 className="text-sm font-semibold text-white mb-3">Reward history</h4>

      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : items.length === 0 ? (
        isError ? (
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs text-red-400 hover:underline"
          >
            Couldn't load history. Retry
          </button>
        ) : (
          <p className="text-xs text-gray-400">
            No rewards yet — complete a {requiredDays}-day streak to earn your first bonus
          </p>
        )
      ) : (
        <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {items.map((item) => (
            <li
              key={item._id}
              className="flex items-center gap-2 text-xs text-gray-300"
            >
              <span aria-hidden="true">🔥</span>
              <span>{formatStreakHistoryRow(item)}</span>
            </li>
          ))}
          {isError ? (
            <li>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="text-xs text-red-400 hover:underline disabled:opacity-50"
              >
                {isFetching ? "Loading…" : "Couldn't load more. Retry"}
              </button>
            </li>
          ) : (
            hasMore && (
              <li>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isFetching}
                  className="text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
                >
                  {isFetching ? "Loading…" : "Load more"}
                </button>
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default StreakRewardHistory;
